import api from './api'
import { saveBlob } from '../../lib/download'

export interface CreateInvoiceData {
  invoice_number: string
  issue_date: string
  due_date: string
  total_amount: number
  payment_status: 'paid' | 'unpaid' | 'partial'
}

export interface InvoiceResponse {
  success: boolean
  data?: any
  message?: string
  errors?: string[]
}

/** Filters for the invoices/quotes list. The two reseller keys are the reseller
 * profile's (2026-09-12: ONE side — XOP's documents TO the reseller).
 *
 *   `bill_to_reseller_id`  — documents XOP addressed TO the reseller (the
 *                            profile's Quotes + Invoices tabs; a portal quote
 *                            request lands here with the same number).
 *   `issuer_reseller_id`   — documents the reseller ISSUED to its customers
 *                            (legacy direction; no CRM surface lists it now).
 *   `customer_reseller_id` — documents issued TO a customer it manages.
 */
export interface InvoiceListFilters {
  document_type?: 'invoice' | 'quote'
  customer_id?: string
  issuer?: 'xop' | 'reseller'
  issuer_reseller_id?: string
  customer_reseller_id?: string
  bill_to_reseller_id?: string
  search?: string
  page?: number
  per_page?: number
  sort?: string
  dir?: 'asc' | 'desc'
  include_customer?: boolean
}

export interface InvoiceListPage {
  data: any[]
  meta?: { pagination?: { current_page: number; per_page: number; total_count: number; total_pages: number } }
}

export const invoicesApi = {
  /** A reseller-billed quote (no customer) → invoice, server-side. crm-api
   * copies the lines + bill-to block, numbers the invoice from the reseller's
   * code and marks the quote `converted`; a customer quote is refused
   * (`NOT_A_RESELLER_QUOTE`) — those convert in the generator. */
  convertResellerQuote: async (id: string | number): Promise<{ invoice: any; quote: any }> => {
    const response = await api.post(`/invoices/${id}/convert`)
    return response.data
  },

  /** One page of invoices or quotes. */
  list: async (filters: InvoiceListFilters = {}): Promise<InvoiceListPage> => {
    const params: Record<string, string> = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params[key] = String(value)
    })
    const response = await api.get('/invoices', { params })
    return response.data
  },

  // Create invoice for a specific deal
  createForDeal: async (dealId: string, customerId: string, invoiceData: CreateInvoiceData, file?: File | null): Promise<InvoiceResponse> => {
    try {
      let requestData: any
      let headers: any = {}

      if (file) {
        // Use FormData for file upload
        const formData = new FormData()
        formData.append('invoice[invoice_number]', invoiceData.invoice_number)
        formData.append('invoice[issue_date]', invoiceData.issue_date)
        formData.append('invoice[due_date]', invoiceData.due_date)
        formData.append('invoice[total_amount]', invoiceData.total_amount.toString())
        formData.append('invoice[payment_status]', invoiceData.payment_status)
        formData.append('invoice[customer_deal_id]', dealId)
        formData.append('invoice[customer_id]', customerId)
        formData.append('file', file)

        requestData = formData
        headers['Content-Type'] = 'multipart/form-data'
      } else {
        // Use JSON for no file
        requestData = {
          invoice: {
            ...invoiceData,
            customer_deal_id: dealId,
            customer_id: customerId
          }
        }
      }

      const response = await api.post('/invoices', requestData, {
        headers
      })
      
      return {
        success: true,
        data: response.data,
        message: 'Invoice created successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to create invoice']
      }
    }
  },

  // Mark invoice as paid
  markAsPaid: async (invoiceId: string, paidDate?: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/mark_as_paid`, {
        paid_date: paidDate || new Date().toISOString().split('T')[0] // Use provided date or current date
      })
      return {
        success: true,
        data: response.data,
        message: 'Invoice marked as paid'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to mark invoice as paid']
      }
    }
  },

  // Mark invoice as unpaid
  markAsUnpaid: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/mark_as_unpaid`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice marked as unpaid'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to mark invoice as unpaid']
      }
    }
  },

  // Get invoice details
  getById: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.get(`/invoices/${invoiceId}`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice retrieved successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to get invoice']
      }
    }
  },

  // Update invoice
  update: async (invoiceId: string, invoiceData: any, file?: File): Promise<InvoiceResponse> => {
    try {
      let requestData: FormData | any
      
      if (file) {
        // If file is provided, use FormData
        requestData = new FormData()
        Object.keys(invoiceData).forEach(key => {
          if (invoiceData[key] !== null && invoiceData[key] !== undefined) {
            requestData.append(key, invoiceData[key].toString())
          }
        })
        requestData.append('invoice_file', file)
      } else {
        // If no file, send as JSON wrapped in invoice key
        requestData = { invoice: invoiceData }
      }

      const response = await api.put(`/invoices/${invoiceId}`, requestData, {
        headers: file ? {} : { 'Content-Type': 'application/json' }
      })
      
      return {
        success: true,
        data: response.data,
        message: 'Invoice updated successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to update invoice']
      }
    }
  },

  // Delete invoice
  delete: async (invoiceId: string): Promise<InvoiceResponse> => {
    try {
      const response = await api.delete(`/invoices/${invoiceId}`)
      return {
        success: true,
        data: response.data,
        message: 'Invoice deleted successfully'
      }
    } catch (error: any) {
      return {
        success: false,
        errors: error.response?.data?.errors || [error.message || 'Failed to delete invoice']
      }
    }
  },

  // Get invoice by ID (returns data directly)
  getInvoiceById: async (invoiceId: string): Promise<any> => {
    const response = await invoicesApi.getById(invoiceId);
    if (!response.success) {
      throw new Error(response.errors?.[0] || 'Failed to get invoice');
    }
    return response.data;
  },

  // Download invoice file via secure custom endpoint
  downloadFile: async (invoiceId: string, originalFilename?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      
      // Just download the file - the backend IS sending the correct filename in Content-Disposition header
      const response = await api.get(`/invoices/${invoiceId}/download`, {
        responseType: 'blob', // Important for binary file data
        timeout: 30000 // 30 second timeout for large files
      })
      
      
      // Get content type from response headers (case-insensitive)
      const responseContentType = response.headers['content-type'] || response.headers['Content-Type'] || 'application/octet-stream'
      
      // Create blob with PROPER content type - this is crucial!
      const blob = new Blob([response.data], { type: responseContentType })

      
      // Extract filename from Content-Disposition header - THE BACKEND IS SENDING THIS CORRECTLY!
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      
      
      let filename = `invoice-${invoiceId}.pdf` // fallback
      
      // First priority: use the original filename if provided
      if (originalFilename && originalFilename.trim()) {
        filename = originalFilename.trim()
      } else if (contentDisposition) {
        // Simple regex to extract filename
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '').trim()
        } else {
          console.warn('❌ Failed to parse filename from Content-Disposition:', contentDisposition)
        }
      } else {
        console.warn('❌ NO Content-Disposition header found and no original filename provided!')
        console.warn('Available headers:', Object.keys(response.headers))
      }
      
      
      // Native OS Save-As dialog in the desktop app; browser download on the web.
      await saveBlob(filename, blob)

      return { success: true }
      
    } catch (error: any) {
      console.error('❌ Secure download error:', error)
      
      if (error.response?.status === 403) {
        return { 
          success: false, 
          error: 'Access denied - insufficient permissions' 
        }
      } else if (error.response?.status === 404) {
        return { 
          success: false, 
          error: 'No file attached to this invoice' 
        }
      } else if (error.response?.status === 401) {
        return { 
          success: false, 
          error: 'Authentication required' 
        }
      }
      
      return { 
        success: false, 
        error: error.message || 'Failed to download invoice file' 
      }
    }
  }
}
